import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Typography, Box } from '@mui/material';
import { Wand2 } from 'lucide-react';

interface GenerateWorkflowModalProps {
  open: boolean;
  onClose: () => void;
  prompt: string;
  onPromptChange: (value: string) => void;
  onGenerate: () => void;
  isGenerating: boolean;
}

const GenerateWorkflowModal: React.FC<GenerateWorkflowModalProps> = ({
  open,
  onClose,
  prompt,
  onPromptChange,
  onGenerate,
  isGenerating,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        color: 'primary.main',
        fontWeight: 'bold'
      }}>
        <Box sx={{ 
          p: 1, 
          borderRadius: '50%', 
          bgcolor: 'primary.light',
          display: 'flex'
        }}>
          <Wand2 size={24} color="#ce00b3" />
        </Box>
        Generate Workflow using AI
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          Enter a description of the workflow you want to generate. Be as specific as possible about the tasks, 
          inputs, outputs, and any special requirements.
        </Typography>
        
        <TextField
          fullWidth
          multiline
          rows={8}
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          placeholder="Describe the workflow you want to generate... (e.g., 'Create a workflow that takes a URL as input, extracts the text content, summarizes it, and translates the summary to Spanish.')"
          variant="outlined"
          sx={{ 
            mb: 2,
            '& .MuiInputBase-input': {
              fontFamily: 'monospace',
              fontSize: '1rem'
            }
          }}
        />
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          variant="outlined" 
          onClick={onClose}
          sx={{ borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onGenerate}
          disabled={isGenerating || !prompt.trim()}
          startIcon={<Wand2 size={16} />}
          sx={{ 
            borderRadius: 2,
            background: 'linear-gradient(45deg, #ce00b3 30%, #9c27b0 90%)',
            boxShadow: '0 3px 5px 2px rgba(206, 0, 179, .3)',
            color: 'white',
            '&:hover': {
              background: 'linear-gradient(45deg, #b8009e 30%, #7b1fa2 90%)',
            }
          }}
        >
          {isGenerating ? 'Generating...' : 'Generate Workflow'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GenerateWorkflowModal;